import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { change_Current_Location, changeHeaderTitle } from '../store';
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { ArrowRightLeft, Search, RefreshCw, AlertCircle, CheckCircle, Calculator, ChevronLeft, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { LoadingModal } from './component/modal/modal';

dayjs.extend(utc);
dayjs.extend(timezone);

const fmtNum = (v) => (Number(v) || 0).toLocaleString();
const PG_FEE_RATE = 1.705;

function DirectSettlement() {
    const dispatch = useDispatch();

    // 폼 (공통)
    const [amount, setAmount] = useState('');
    const [feeRate, setFeeRate] = useState('');
    const [bankInfoList, setBankInfoList] = useState([]);
    const [rvAccountCntn, setRvAccountCntn] = useState('정산대금');
    const [memo, setMemo] = useState('');
    const [loading, setLoading] = useState(false);

    // 수신자 목록
    const emptyRecipient = () => ({ rvBankCode: '', bankSearchText: '', showBankDropdown: false, rvAccount: '', accountHolder: '', holderVerified: false, holderLoading: false, amount: '' });
    const [recipients, setRecipients] = useState([emptyRecipient()]);
    const [activeIdx, setActiveIdx] = useState(0);

    const MAX_RECIPIENTS = 2;
    const addRecipient = () => {
        if (recipients.length >= MAX_RECIPIENTS) return;
        setRecipients(prev => [...prev, emptyRecipient()]);
        setActiveIdx(recipients.length);
    };
    const removeRecipient = (idx) => {
        if (recipients.length <= 1) return;
        setRecipients(prev => prev.filter((_, i) => i !== idx));
        if (activeIdx === idx) setActiveIdx(Math.max(0, idx - 1));
        else if (activeIdx > idx) setActiveIdx(prev => prev - 1);
    };
    const updateRecipient = (idx, field, value) => setRecipients(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));

    const recipientTotal = useMemo(() => recipients.reduce((sum, r) => sum + (Number(r.amount) || 0), 0), [recipients]);

    // OTP
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [otpToken, setOtpToken] = useState('');

    // 토스트
    const [toast, setToast] = useState(null);

    // 내역
    const [logs, setLogs] = useState([]);
    const [summary, setSummary] = useState(null);
    const [page, setPage] = useState(1);
    const [totPage, setTotPage] = useState(1);
    const [totCnt, setTotCnt] = useState(0);
    const [logsLoading, setLogsLoading] = useState(false);

    // 필터
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    useEffect(() => {
        dispatch(change_Current_Location("직접 정산"));
        dispatch(changeHeaderTitle({ mainTitle: "직접 정산", subTitle: "직접 정산 관리" }));
        fetchBankInfo();
        loadLogs(1);
    }, []);

    // 토스트 자동 닫힘
    useEffect(() => {
        if (toast) {
            const timer = setTimeout(() => setToast(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [toast]);

    // 은행 드롭다운 외부 클릭
    useEffect(() => {
        const handleClick = () => setRecipients(prev => prev.map(r => ({ ...r, showBankDropdown: false })));
        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, []);

    // ── 실시간 계산 ──
    const calc = useMemo(() => {
        const amt = Number(amount) || 0;
        const rate = parseFloat(feeRate) || 0;
        if (amt <= 0 || rate <= 0) return null;
        const fee = Math.round(amt * rate / 100);
        const pgFee = Math.round(amt * PG_FEE_RATE / 100);
        const profit = fee - pgFee;
        const transfer = amt - fee;
        return { fee, pgFee, profit, transfer };
    }, [amount, feeRate]);

    // ── 은행 목록 ──
    const fetchBankInfo = async () => {
        try {
            const res = await axios.get(process.env.REACT_APP_API + "/api/payout/bank-info", { withCredentials: true });
            if (res.data?.code < 2000) {
                setBankInfoList(res.data.data?.bank_info || []);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const getFilteredBanks = (searchText) => bankInfoList.filter(b => {
        const q = (searchText || '').toLowerCase();
        return b.bank_name?.toLowerCase().includes(q) || b.code?.includes(q);
    });

    const handleBankSelect = (idx, code, name) => {
        setRecipients(prev => prev.map((r, i) => i === idx ? { ...r, rvBankCode: code, bankSearchText: name, showBankDropdown: false } : r));
    };

    const getBankName = (code) => {
        const found = bankInfoList.find(b => b.code === code);
        return found ? found.bank_name : code;
    };

    // ── 예금주 조회 ──
    const handleCheckAccountHolder = async (idx) => {
        const r = recipients[idx];
        if (!r.rvBankCode) {
            setToast({ type: 'error', title: '입력 오류', text: '은행을 먼저 선택해주세요.' });
            return;
        }
        if (!/^\d{10,20}$/.test(r.rvAccount)) {
            setToast({ type: 'error', title: '입력 오류', text: '계좌번호는 10-20자리 숫자여야 합니다.' });
            return;
        }
        updateRecipient(idx, 'holderLoading', true);
        try {
            const res = await axios.post(
                process.env.REACT_APP_API + "/api/transfer/account-holder",
                { bankCode: r.rvBankCode, account: r.rvAccount },
                { withCredentials: true }
            );
            if (res.data?.code === 1000) {
                setRecipients(prev => prev.map((rec, i) => i === idx ? { ...rec, accountHolder: res.data.data.depositor, holderVerified: true, holderLoading: false } : rec));
                setToast({ type: 'success', title: '조회 완료', text: `예금주: ${res.data.data.depositor}` });
            } else {
                setRecipients(prev => prev.map((rec, i) => i === idx ? { ...rec, accountHolder: '', holderVerified: false, holderLoading: false } : rec));
                setToast({ type: 'error', title: '조회 실패', text: res.data?.msg || '예금주 조회에 실패했습니다.' });
            }
        } catch (err) {
            console.error(err);
            setRecipients(prev => prev.map((rec, i) => i === idx ? { ...rec, accountHolder: '', holderVerified: false, holderLoading: false } : rec));
            setToast({ type: 'error', title: '오류', text: '예금주 조회 중 오류가 발생했습니다.' });
        }
    };

    // ── 검증 ──
    const handleValidate = () => {
        const amt = Number(amount);
        if (!amt || amt <= 0) {
            setToast({ type: 'error', title: '입력 오류', text: '입금액을 입력해주세요.' });
            return;
        }
        const rate = parseFloat(feeRate);
        if (!rate || rate <= PG_FEE_RATE) {
            setToast({ type: 'error', title: '입력 오류', text: `수수료율은 ${PG_FEE_RATE}%보다 커야 합니다.` });
            return;
        }
        if (calc && calc.transfer <= 0) {
            setToast({ type: 'error', title: '입력 오류', text: '수수료 차감 후 이체 금액이 0 이하입니다.' });
            return;
        }
        for (let i = 0; i < recipients.length; i++) {
            const r = recipients[i];
            if (!r.rvBankCode) { setToast({ type: 'error', title: '입력 오류', text: `수신자 ${i + 1}: 은행을 선택해주세요.` }); return; }
            if (!r.rvAccount) { setToast({ type: 'error', title: '입력 오류', text: `수신자 ${i + 1}: 계좌번호를 입력해주세요.` }); return; }
            if (!r.amount || Number(r.amount) <= 0) { setToast({ type: 'error', title: '입력 오류', text: `수신자 ${i + 1}: 금액을 입력해주세요.` }); return; }
        }
        if (calc && recipientTotal !== calc.transfer) {
            setToast({ type: 'error', title: '금액 불일치', text: `수신자 합계(${fmtNum(recipientTotal)}원)가 실이체액(${fmtNum(calc.transfer)}원)과 일치하지 않습니다.` });
            return;
        }
        setOtpToken('');
        setIdempotencyKey(generateUUID());
        setShowOtpModal(true);
    };

    // ── UUID 생성 (Idempotency-Key) ──
    const generateUUID = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    };

    // ── 정산 실행 ──
    const [idempotencyKey, setIdempotencyKey] = useState('');

    const handleExecute = async () => {
        if (otpToken.length !== 6 || loading) return;
        setLoading(true);

        const key = idempotencyKey || generateUUID();
        setIdempotencyKey(key);

        try {
            const res = await axios.post(
                process.env.REACT_APP_API + "/api/transfer/direct-settlement",
                {
                    otpToken,
                    idempotencyKey: key,
                    amount: Number(amount),
                    feeRate: parseFloat(feeRate),
                    rvAccountCntn: rvAccountCntn || "정산대금",
                    memo,
                    recipients: recipients.map(r => ({
                        rvBankCode: r.rvBankCode,
                        rvAccount: r.rvAccount,
                        accountHolder: r.accountHolder,
                        amount: Number(r.amount),
                    })),
                },
                {
                    withCredentials: true,
                    headers: { 'Idempotency-Key': key }
                }
            );
            const tmp = res.data;
            if (tmp?.code < 2000) {
                const d = tmp.data;
                if (d.status === 'SUCCESS') {
                    setToast({
                        type: 'success', title: '정산 완료',
                        text: `이체액 ${fmtNum(d.transferAmount)}원 / 수익 ${fmtNum(d.profitAmount)}원`
                    });
                } else if (d.status === 'PENDING') {
                    setToast({ type: 'warning', title: '처리 대기', text: '정산 요청이 접수되었습니다. 처리 결과를 확인해주세요.' });
                }
                resetForm();
                setShowOtpModal(false);
                setIdempotencyKey('');
                loadLogs(1);
            } else {
                setToast({ type: 'error', title: '정산 실패', text: tmp?.msg || '정산에 실패했습니다.' });
                setOtpToken('');
            }
        } catch (err) {
            console.error(err);
            if (err.response?.status === 409) {
                setToast({ type: 'error', title: '중복 요청', text: '이미 처리 중인 요청입니다. 잠시 후 내역을 확인해주세요.' });
                setShowOtpModal(false);
                setIdempotencyKey('');
                loadLogs(1);
            } else {
                const msg = err.response?.data?.msg || '서버 통신 오류';
                setToast({ type: 'error', title: '오류', text: msg });
                setOtpToken('');
            }
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setAmount('');
        setFeeRate('');
        setRecipients([emptyRecipient()]);
        setRvAccountCntn('정산대금');
        setMemo('');
        setIdempotencyKey('');
    };

    // ── 내역 조회 ──
    const loadLogs = async (p) => {
        setLogsLoading(true);
        const start = Date.now();
        try {
            const params = { page: p, limit: 20 };
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;
            if (statusFilter) params.status = statusFilter;

            const res = await axios.get(
                process.env.REACT_APP_API + "/api/transfer/direct-settlement/logs",
                { params, withCredentials: true }
            );
            const tmp = res.data;
            if (tmp?.code < 2000) {
                setLogs(tmp.data.list || []);
                setSummary(tmp.data.summary || null);
                setPage(tmp.data.curPage || 1);
                setTotPage(tmp.data.totPage || 1);
                setTotCnt(tmp.data.totCnt || 0);
            }
        } catch (err) {
            console.error(err);
        } finally {
            const elapsed = Date.now() - start;
            if (elapsed < 500) await new Promise(r => setTimeout(r, 500 - elapsed));
            setLogsLoading(false);
        }
    };

    const changeOtpToken = (e) => {
        const v = e.target.value.replace(/[^0-9]/g, '');
        if (v.length <= 6) setOtpToken(v);
    };

    return (
        <div className="direct-settlement-container">
            {logsLoading ? <LoadingModal /> : ""}

            {/* 토스트 */}
            {toast && (
                <div className={`toast toast-${toast.type}`}>
                    <div className="toast-content">
                        <div className="toast-icon">
                            {toast.type === 'success' ? <CheckCircle size={20} /> :
                                toast.type === 'warning' ? <AlertCircle size={20} /> :
                                    <AlertCircle size={20} />}
                        </div>
                        <div className="toast-message">
                            <div className="toast-title">{toast.title}</div>
                            <div className="toast-text">{toast.text}</div>
                        </div>
                        <button className="toast-close" onClick={() => setToast(null)}>×</button>
                    </div>
                </div>
            )}

            {/* 2단 그리드 */}
            <div className="settlement-grid">

                {/* ── 좌측: 정산 폼 ── */}
                <div className="card">
                    <div className="card-header">
                        <Calculator size={18} />
                        <h3>정산 실행</h3>
                    </div>
                    <div className="card-content">
                        <div className="transfer-form">

                            <div className="form-row">
                                <div className="form-group" style={{ flex: 1 }}>
                                    <label>입금액 (원) <span style={{ color: '#ef4444' }}>*</span></label>
                                    <input
                                        type="number"
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        placeholder="입금액"
                                        disabled={loading}
                                    />
                                </div>
                                <div className="form-group" style={{ flex: 1 }}>
                                    <label>수수료율 (%) <span style={{ color: '#ef4444' }}>*</span></label>
                                    <input
                                        type="number"
                                        step="0.001"
                                        min={PG_FEE_RATE}
                                        max={50}
                                        value={feeRate}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            if (v === '' || parseFloat(v) <= 50) setFeeRate(v);
                                        }}
                                        onBlur={() => {
                                            const v = parseFloat(feeRate);
                                            if (feeRate !== '' && v < PG_FEE_RATE) setFeeRate(String(PG_FEE_RATE));
                                            if (v > 50) setFeeRate('50');
                                        }}
                                        placeholder={`${PG_FEE_RATE} ~ 50%`}
                                        disabled={loading}
                                    />
                                </div>
                            </div>

                            {/* 실시간 계산 미리보기 */}
                            {calc && (
                                <div className="calc-preview">
                                    <div className="calc-row">
                                        <span className="calc-label">수수료</span>
                                        <span className="calc-value">{fmtNum(calc.fee)}원</span>
                                    </div>
                                    <div className="calc-row">
                                        <span className="calc-label">PG 수수료 ({PG_FEE_RATE}%)</span>
                                        <span className="calc-value">{fmtNum(calc.pgFee)}원</span>
                                    </div>
                                    <div className="calc-row">
                                        <span className="calc-label">수익</span>
                                        <span className="calc-value" style={{ color: '#059669', fontWeight: 700 }}>{fmtNum(calc.profit)}원</span>
                                    </div>
                                    <div className="calc-divider" />
                                    <div className="calc-row">
                                        <span className="calc-label" style={{ fontWeight: 700 }}>실 이체액</span>
                                        <span className="calc-value" style={{ color: '#1a56db', fontWeight: 700, fontSize: '13px' }}>{fmtNum(calc.transfer)}원</span>
                                    </div>
                                </div>
                            )}

                            {/* ── 수신자 목록 ── */}
                            <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '-4px' }}>
                                수신자 ({recipients.length}건)
                            </div>
                            {recipients.map((r, idx) => {
                                const isActive = idx === activeIdx;
                                const isComplete = r.rvBankCode && r.rvAccount && r.amount;
                                const filtered = getFilteredBanks(r.bankSearchText);

                                // 접힌 상태
                                if (!isActive) {
                                    return (
                                        <div key={idx}
                                            className={`recipient-collapsed ${r.holderVerified ? 'verified' : 'incomplete'}`}
                                            onClick={() => setActiveIdx(idx)}
                                        >
                                            <span className="recipient-idx">#{idx + 1}</span>
                                            <span>{getBankName(r.rvBankCode) || '미입력'}</span>
                                            <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>{r.rvAccount ? '****' + r.rvAccount.slice(-4) : '-'}</span>
                                            <span>{r.accountHolder || '-'}</span>
                                            <span className="recipient-amount">{r.amount ? fmtNum(r.amount) + '원' : '-'}</span>
                                            {recipients.length > 1 && (
                                                <button onClick={(e) => { e.stopPropagation(); removeRecipient(idx); }}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db', padding: '2px', marginLeft: '2px' }}>
                                                    <Trash2 size={12} />
                                                </button>
                                            )}
                                        </div>
                                    );
                                }

                                // 펼친 상태
                                return (
                                    <div className="recipient-card" key={idx}>
                                        <div className="recipient-header">
                                            <span style={{ fontSize: '11px', color: '#1a56db', fontWeight: 600 }}>#{idx + 1} 편집 중</span>
                                            {recipients.length > 1 && (
                                                <button onClick={() => removeRecipient(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '2px' }}>
                                                    <Trash2 size={13} />
                                                </button>
                                            )}
                                        </div>
                                        <div className="recipient-fields">
                                            <div className="form-group" style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                                                <div className="bank-search-container">
                                                    <input type="text" className="bank-search-input" placeholder="은행"
                                                        value={r.bankSearchText}
                                                        onChange={(e) => { updateRecipient(idx, 'bankSearchText', e.target.value); updateRecipient(idx, 'showBankDropdown', true); }}
                                                        onFocus={() => updateRecipient(idx, 'showBankDropdown', true)}
                                                        disabled={loading} autoComplete="off" />
                                                    {r.showBankDropdown && filtered.length > 0 && (
                                                        <div className="bank-dropdown">
                                                            {filtered.map(bank => (
                                                                <div key={bank.code} className={`bank-dropdown-item ${r.rvBankCode === bank.code ? 'selected' : ''}`}
                                                                    onClick={() => handleBankSelect(idx, bank.code, bank.bank_name)}>
                                                                    {bank.bank_name}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {r.showBankDropdown && filtered.length === 0 && r.bankSearchText && (
                                                        <div className="bank-dropdown"><div className="bank-dropdown-item no-results">없음</div></div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="form-group">
                                                <div className="input-with-button">
                                                    <input type="text" value={r.rvAccount} placeholder="계좌번호"
                                                        onChange={(e) => { updateRecipient(idx, 'rvAccount', e.target.value.replace(/[^0-9]/g, '')); updateRecipient(idx, 'holderVerified', false); updateRecipient(idx, 'accountHolder', ''); }}
                                                        disabled={loading} />
                                                    <button className="btn-check" onClick={() => handleCheckAccountHolder(idx)} disabled={loading || r.holderLoading || !r.rvAccount}>
                                                        {r.holderLoading ? '...' : <Search size={13} />}
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="form-group">
                                                {r.holderVerified ? (
                                                    <div className="account-holder-verified">
                                                        <CheckCircle size={12} /><span>{r.accountHolder}</span>
                                                    </div>
                                                ) : (
                                                    <input type="text" value={r.accountHolder} placeholder="예금주"
                                                        onChange={(e) => updateRecipient(idx, 'accountHolder', e.target.value)} disabled={loading} />
                                                )}
                                            </div>
                                            <div className="form-group">
                                                <input type="number" value={r.amount} placeholder="금액"
                                                    onChange={(e) => updateRecipient(idx, 'amount', e.target.value)} disabled={loading} />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            <button onClick={addRecipient} disabled={loading || recipients.length >= MAX_RECIPIENTS} className="btn-add-recipient">
                                <Plus size={13} /> 수신자 추가
                            </button>

                            {/* 합계 검증 */}
                            {calc && (
                                <div className="recipients-total" style={{ color: recipientTotal === calc.transfer ? '#059669' : '#ef4444' }}>
                                    수신자 합계: {fmtNum(recipientTotal)}원 / 실이체액: {fmtNum(calc.transfer)}원
                                    {recipientTotal === calc.transfer ? ' ✓' : ` (${fmtNum(calc.transfer - recipientTotal)}원 ${recipientTotal < calc.transfer ? '부족' : '초과'})`}
                                </div>
                            )}

                            <div className="form-row">
                                <div className="form-group" style={{ flex: 1 }}>
                                    <label>적요</label>
                                    <input
                                        type="text"
                                        value={rvAccountCntn}
                                        onChange={(e) => {
                                            if (e.target.value.length <= 10) setRvAccountCntn(e.target.value);
                                        }}
                                        placeholder="정산대금"
                                        disabled={loading}
                                    />
                                </div>
                                <div className="form-group" style={{ flex: 1 }}>
                                    <label>메모</label>
                                    <input
                                        type="text"
                                        value={memo}
                                        onChange={(e) => setMemo(e.target.value)}
                                        placeholder="메모 입력"
                                        disabled={loading}
                                    />
                                </div>
                            </div>

                            <button
                                className="btn-transfer"
                                onClick={handleValidate}
                                disabled={loading}
                            >
                                정산 실행
                            </button>
                        </div>
                    </div>
                </div>

                {/* ── 우측: 정산 내역 ── */}
                <div className="card">
                    <div className="card-header">
                        <ArrowRightLeft size={18} />
                        <h3>정산 내역</h3>
                        <button className="btn-refresh" onClick={() => loadLogs(page)} disabled={logsLoading}>
                            <RefreshCw size={16} className={logsLoading ? 'spinning' : ''} />
                        </button>
                    </div>
                    <div className="card-content">

                        {/* 필터 */}
                        <div className="log-filters">
                            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                            <span style={{ color: '#9ca3af' }}>~</span>
                            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                <option value="">전체</option>
                                <option value="SUCCESS">성공</option>
                                <option value="PENDING">대기</option>
                                <option value="FAILED">실패</option>
                            </select>
                            <button className="btn-search" onClick={() => loadLogs(1)}>
                                <Search size={14} /> 조회
                            </button>
                        </div>

                        <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
                            총 <strong style={{ color: '#111' }}>{totCnt}</strong>건
                        </div>

                        {/* 테이블 */}
                        <div className="transfer-history" style={{ overflowX: 'auto' }}>
                            <table>
                                <thead>
                                    <tr>
                                        <th>배치</th>
                                        <th>일시</th>
                                        <th style={{ textAlign: 'right' }}>입금액</th>
                                        <th style={{ textAlign: 'right' }}>수수료율</th>
                                        <th style={{ textAlign: 'right' }}>수수료</th>
                                        <th style={{ textAlign: 'right' }}>PG수수료</th>
                                        <th style={{ textAlign: 'right' }}>수익</th>
                                        <th style={{ textAlign: 'right' }}>이체액</th>
                                        <th>은행</th>
                                        <th>계좌</th>
                                        <th>예금주</th>
                                        <th>상태</th>
                                        <th>메모</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logsLoading ? (
                                        <tr><td colSpan={13} className="empty-state">조회 중...</td></tr>
                                    ) : logs.length === 0 ? (
                                        <tr><td colSpan={13} className="empty-state">데이터가 없습니다</td></tr>
                                    ) : logs.map((item, idx) => {
                                        const prevBatch = idx > 0 ? logs[idx - 1].batch_id : null;
                                        const isNewBatch = item.batch_id !== prevBatch;
                                        return (
                                        <tr key={item.id} style={!isNewBatch && item.batch_id ? { background: '#fafafa' } : {}}>
                                            <td style={{ whiteSpace: 'nowrap', color: '#9ca3af', fontSize: '10px' }}>
                                                {isNewBatch && item.batch_id ? item.batch_id.slice(-6) : ''}
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                {item.created_at ? dayjs.utc(item.created_at).tz("Asia/Seoul").format("MM-DD HH:mm") : "-"}
                                            </td>
                                            <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtNum(item.input_amount)}</td>
                                            <td style={{ textAlign: 'right' }}>{item.fee_rate}%</td>
                                            <td style={{ textAlign: 'right' }}>{fmtNum(item.fee_amount)}</td>
                                            <td style={{ textAlign: 'right' }}>{fmtNum(item.pg_fee_amount)}</td>
                                            <td style={{ textAlign: 'right', color: '#059669', fontWeight: 600 }}>{fmtNum(item.profit_amount)}</td>
                                            <td style={{ textAlign: 'right', color: '#1a56db', fontWeight: 600 }}>{fmtNum(item.transfer_amount)}</td>
                                            <td style={{ whiteSpace: 'nowrap' }}>{getBankName(item.rv_bank_code)}</td>
                                            <td style={{ fontFamily: 'monospace', fontSize: '11px' }}>{item.rv_account}</td>
                                            <td>{item.account_holder || '-'}</td>
                                            <td>
                                                <span className={`status status-${(item.transfer_status || '').toLowerCase()}`}>
                                                    {item.transfer_status === 'SUCCESS' ? '성공' :
                                                        item.transfer_status === 'PENDING' ? '대기' :
                                                            item.transfer_status === 'FAILED' ? '실패' : item.transfer_status}
                                                </span>
                                            </td>
                                            <td style={{ maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {item.memo || '-'}
                                            </td>
                                        </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* 요약 */}
                        {summary && (
                            <div className="summary-bar">
                                <div className="summary-item">
                                    <span className="summary-label">총입금액</span>
                                    <span className="summary-value">{fmtNum(summary.total_input)}원</span>
                                </div>
                                <div className="summary-item">
                                    <span className="summary-label">총이체액</span>
                                    <span className="summary-value" style={{ color: '#1a56db' }}>{fmtNum(summary.total_transfer)}원</span>
                                </div>
                                <div className="summary-item">
                                    <span className="summary-label">총수익</span>
                                    <span className="summary-value" style={{ color: '#059669' }}>{fmtNum(summary.total_profit)}원</span>
                                </div>
                                <div className="summary-item">
                                    <span className="summary-label">총PG수수료</span>
                                    <span className="summary-value" style={{ color: '#ef4444' }}>{fmtNum(summary.total_pg_fee)}원</span>
                                </div>
                            </div>
                        )}

                        {/* 페이징 */}
                        {totPage > 1 && (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '16px' }}>
                                <button className="btn-page" onClick={() => loadLogs(page - 1)} disabled={page <= 1}>
                                    <ChevronLeft size={16} />
                                </button>
                                <span style={{ fontSize: '13px', color: '#6b7280' }}>{page} / {totPage}</span>
                                <button className="btn-page" onClick={() => loadLogs(page + 1)} disabled={page >= totPage}>
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* OTP 모달 */}
            {showOtpModal && (
                <div className="otp-modal-overlay" onClick={() => setShowOtpModal(false)}>
                    <div className="otp-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="otp-modal-header">
                            <h3>OTP 인증</h3>
                            <button className="otp-modal-close" onClick={() => { setShowOtpModal(false); setOtpToken(''); }}>×</button>
                        </div>
                        <div className="otp-modal-body">
                            <p style={{ marginBottom: '20px', color: '#666' }}>
                                정산을 완료하기 위해 OTP 코드를 입력해주세요.
                            </p>
                            {calc && (
                                <div style={{
                                    padding: '12px 16px', marginBottom: '20px',
                                    background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', fontSize: '12px',
                                }}>
                                    <div style={{ marginBottom: '4px' }}>입금액: <strong>{fmtNum(amount)}원</strong> → 이체액: <strong style={{ color: '#1a56db' }}>{fmtNum(calc.transfer)}원</strong> / 수익: <strong style={{ color: '#059669' }}>{fmtNum(calc.profit)}원</strong></div>
                                    {recipients.map((r, i) => (
                                        <div key={i} style={{ color: '#555', fontSize: '11px' }}>
                                            #{i + 1} {getBankName(r.rvBankCode)} {r.rvAccount} {r.accountHolder || '-'} {fmtNum(r.amount)}원
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="form-group">
                                <label>OTP 코드 (6자리)</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="000000"
                                    maxLength={6}
                                    value={otpToken}
                                    onChange={changeOtpToken}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && otpToken.length === 6 && !loading) {
                                            handleExecute();
                                        }
                                    }}
                                    style={{ textAlign: "center", fontSize: "24px", letterSpacing: "8px", padding: "15px" }}
                                    disabled={loading}
                                    autoFocus
                                />
                            </div>
                        </div>
                        <div className="otp-modal-footer">
                            <button
                                className="btn-transfer"
                                onClick={handleExecute}
                                disabled={loading || otpToken.length !== 6}
                            >
                                {loading ? '처리 중...' : '정산 실행'}
                            </button>
                            <button
                                className="btn-cancel"
                                onClick={() => { setShowOtpModal(false); setOtpToken(''); }}
                                disabled={loading}
                            >
                                취소
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                .direct-settlement-container {
                    padding: 20px;
                    margin-top: 80px;
                }

                .page-header {
                    margin-bottom: 20px;
                }

                .page-header h1 {
                    font-size: 18px;
                    font-weight: 600;
                    color: #1f2937;
                    margin-bottom: 4px;
                    text-align: left;
                }

                .page-header p {
                    color: #6b7280;
                    font-size: 13px;
                    text-align: left;
                }

                /* 토스트 */
                .toast {
                    position: fixed;
                    top: 20px;
                    right: 20px;
                    z-index: 10001;
                    max-width: 400px;
                    min-width: 300px;
                    animation: slideInRight 0.3s ease-out;
                }

                .toast-content {
                    display: flex;
                    align-items: flex-start;
                    gap: 12px;
                    padding: 16px;
                    border-radius: 8px;
                    box-shadow: 0 10px 25px -5px rgba(0,0,0,0.1);
                    backdrop-filter: blur(10px);
                }

                .toast-success .toast-content { background: rgba(16,185,129,0.95); color: white; }
                .toast-error .toast-content { background: rgba(239,68,68,0.95); color: white; }
                .toast-warning .toast-content { background: rgba(245,158,11,0.95); color: white; }

                .toast-icon { flex-shrink: 0; margin-top: 2px; }
                .toast-message { flex: 1; }
                .toast-title { font-weight: 600; font-size: 14px; margin-bottom: 4px; }
                .toast-text { font-size: 13px; opacity: 0.9; line-height: 1.4; }

                .toast-close {
                    background: none; border: none; color: white;
                    font-size: 18px; font-weight: bold; cursor: pointer;
                    padding: 0; width: 20px; height: 20px;
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 50%; transition: background-color 0.2s; flex-shrink: 0;
                }
                .toast-close:hover { background: rgba(255,255,255,0.2); }

                @keyframes slideInRight {
                    from { transform: translateX(100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }

                /* 그리드 */
                .settlement-grid {
                    display: grid;
                    grid-template-columns: 30% 70%;
                    gap: 16px;
                    margin-bottom: 20px;
                }

                .card {
                    background: white;
                    border-radius: 8px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                    overflow: hidden;
                }

                .card-header {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 8px 16px;
                    background: #f8fafc;
                    border-bottom: 1px solid #e2e8f0;
                    height: 40px;
                    box-sizing: border-box;
                }

                .card-header h3 {
                    font-size: 14px; font-weight: 600; color: #1f2937; margin: 0;
                }

                .card-content { padding: 16px; }

                /* 폼 */
                .transfer-form {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                }

                .form-group {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }

                .form-row {
                    display: flex;
                    gap: 8px;
                }

                .form-group label {
                    font-weight: 500;
                    color: #374151;
                    font-size: 12px;
                }

                .form-group input, .form-group select {
                    padding: 8px 10px;
                    border: 1px solid #d1d5db;
                    border-radius: 6px;
                    font-size: 13px;
                    transition: border-color 0.2s;
                }

                .form-group input:focus {
                    outline: none;
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 2px rgba(59,130,246,0.1);
                }

                .form-group input:disabled {
                    background-color: #f9fafb;
                    color: #6b7280;
                }

                /* 계산 미리보기 */
                .calc-preview {
                    padding: 10px 12px;
                    background: #f0f9ff;
                    border: 1px solid #bae6fd;
                    border-radius: 6px;
                }

                .calc-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 2px 0;
                    font-size: 12px;
                }

                .calc-label { color: #0369a1; }
                .calc-value { color: #1f2937; font-weight: 500; }

                .calc-divider {
                    border-top: 1px dashed #93c5fd;
                    margin: 6px 0;
                }

                /* 은행 드롭다운 */
                .bank-search-container { position: relative; }

                .bank-search-input {
                    width: 100%;
                    padding: 8px 10px;
                    border: 1px solid #d1d5db;
                    border-radius: 6px;
                    font-size: 13px;
                    transition: border-color 0.2s;
                    box-sizing: border-box;
                }

                .bank-search-input:focus {
                    outline: none;
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 2px rgba(59,130,246,0.1);
                }

                .bank-dropdown {
                    position: absolute;
                    top: 100%; left: 0; right: 0;
                    background: white;
                    border: 1px solid #d1d5db;
                    border-radius: 6px;
                    margin-top: 4px;
                    max-height: 180px;
                    overflow-y: auto;
                    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
                    z-index: 1000;
                }

                .bank-dropdown-item {
                    padding: 8px 10px; cursor: pointer; font-size: 13px;
                    transition: background-color 0.2s;
                    border-bottom: 1px solid #f3f4f6;
                }
                .bank-dropdown-item:last-child { border-bottom: none; }
                .bank-dropdown-item:hover { background-color: #f3f4f6; }
                .bank-dropdown-item.selected { background-color: #eff6ff; color: #2563eb; font-weight: 600; }
                .bank-dropdown-item.no-results { color: #6b7280; cursor: default; text-align: center; }
                .bank-dropdown-item.no-results:hover { background-color: white; }

                /* 수신자 접힌 상태 */
                .recipient-collapsed {
                    display: flex; align-items: center; gap: 6px;
                    padding: 6px 10px; margin-bottom: 4px;
                    background: #fff; border: 1px solid #e5e7eb; border-radius: 6px;
                    border-left: 3px solid #d1d5db;
                    cursor: pointer; font-size: 11px; color: #374151;
                    transition: background 0.15s;
                }
                .recipient-collapsed:hover { background: #f3f4f6; }
                .recipient-collapsed.verified { border-left-color: #22c55e; }
                .recipient-collapsed.incomplete { border-left-color: #f59e0b; }
                .recipient-idx { font-weight: 600; color: #6b7280; min-width: 20px; }
                .recipient-amount { margin-left: auto; font-weight: 600; color: #1a56db; }

                /* 수신자 펼친 카드 */
                .recipient-card {
                    padding: 10px 12px; margin-bottom: 4px;
                    background: #f0f7ff; border: 1px solid #bfdbfe; border-radius: 6px;
                    border-left: 3px solid #1a56db;
                }
                .recipient-header {
                    display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;
                }
                .recipient-fields {
                    display: grid; grid-template-columns: 1fr 1fr; gap: 6px;
                }

                .btn-add-recipient {
                    display: flex; align-items: center; justify-content: center; gap: 4px;
                    width: 100%; padding: 8px; font-size: 12px; font-weight: 500;
                    border: 1px dashed #d1d5db; border-radius: 6px;
                    background: #fff; color: #6b7280; cursor: pointer;
                    transition: all 0.2s;
                }
                .btn-add-recipient:hover { border-color: #3b82f6; color: #3b82f6; }
                .btn-add-recipient:disabled { opacity: 0.5; cursor: not-allowed; }

                .recipients-total {
                    font-size: 12px; font-weight: 600;
                    padding: 8px 10px;
                    background: #f9fafb; border-radius: 6px;
                    text-align: right;
                }

                /* 계좌번호 + 조회 버튼 */
                .input-with-button {
                    display: flex;
                    gap: 6px;
                }
                .input-with-button input { flex: 1; }

                .btn-check {
                    padding: 8px 12px;
                    background: #3b82f6; color: white;
                    border: none; border-radius: 6px;
                    cursor: pointer; font-weight: 500; font-size: 13px;
                    transition: background-color 0.2s;
                    display: flex; align-items: center; justify-content: center;
                }
                .btn-check:hover:not(:disabled) { background: #2563eb; }
                .btn-check:disabled { background: #9ca3af; cursor: not-allowed; }

                /* 예금주 확인 */
                .account-holder-verified {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    padding: 8px 10px;
                    background: #f0fdf4;
                    border: 1px solid #bbf7d0;
                    border-radius: 6px;
                    color: #166534;
                    font-weight: 500;
                    font-size: 13px;
                }

                /* 버튼 */
                .btn-transfer {
                    padding: 10px; background: #059669; color: white;
                    border: none; border-radius: 6px; font-size: 13px;
                    font-weight: 600; cursor: pointer; transition: background-color 0.2s;
                }
                .btn-transfer:hover:not(:disabled) { background: #047857; }
                .btn-transfer:disabled { background: #9ca3af; cursor: not-allowed; }

                .btn-cancel {
                    padding: 10px 16px; background: #f3f4f6; color: #374151;
                    border: none; border-radius: 6px; font-size: 13px;
                    font-weight: 600; cursor: pointer; transition: background-color 0.2s;
                }
                .btn-cancel:hover:not(:disabled) { background: #e5e7eb; }
                .btn-cancel:disabled { opacity: 0.5; cursor: not-allowed; }

                .btn-refresh {
                    margin-left: auto; background: none; border: none;
                    padding: 8px; border-radius: 6px; cursor: pointer;
                    color: #6b7280; transition: all 0.2s;
                }
                .btn-refresh:hover { background: #e2e8f0; color: #374151; }
                .btn-refresh:disabled { opacity: 0.5; cursor: not-allowed; }

                .spinning { animation: spin 1s linear infinite; }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

                /* 필터 */
                .log-filters {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-bottom: 12px;
                    flex-wrap: wrap;
                }

                .log-filters input, .log-filters select {
                    padding: 6px 10px; font-size: 13px;
                    border: 1px solid #d1d5db; border-radius: 6px;
                    outline: none;
                }

                .btn-search {
                    display: inline-flex; align-items: center; gap: 4px;
                    padding: 6px 14px; font-size: 13px; font-weight: 500;
                    border: none; border-radius: 6px;
                    background: #374151; color: #fff; cursor: pointer;
                }

                /* 테이블 */
                .transfer-history table {
                    width: 100%;
                    border-collapse: collapse;
                }

                .transfer-history th,
                .transfer-history td {
                    padding: 6px 6px;
                    text-align: left;
                    border-bottom: 1px solid #e5e7eb;
                    font-size: 11px;
                    white-space: nowrap;
                }

                .transfer-history th {
                    background: #f9fafb;
                    font-weight: 600;
                    color: #374151;
                    font-size: 11px;
                }

                .empty-state {
                    text-align: center;
                    color: #6b7280;
                    padding: 30px;
                    font-size: 13px;
                }

                /* 상태 뱃지 */
                .status {
                    padding: 2px 6px; border-radius: 3px;
                    font-size: 11px; font-weight: 500;
                }
                .status-success { background: #d1fae5; color: #065f46; }
                .status-pending { background: #fef3c7; color: #92400e; }
                .status-failed { background: #fee2e2; color: #991b1b; }

                /* 요약 바 */
                .summary-bar {
                    display: flex;
                    gap: 20px;
                    padding: 10px 12px;
                    margin-top: 10px;
                    background: #f9fafb;
                    border: 1px solid #e5e7eb;
                    border-radius: 6px;
                    flex-wrap: wrap;
                }

                .summary-item {
                    display: flex;
                    flex-direction: column;
                    gap: 1px;
                }

                .summary-label { font-size: 10px; color: #6b7280; }
                .summary-value { font-size: 12px; font-weight: 700; color: #1f2937; }

                /* 페이징 */
                .btn-page {
                    display: inline-flex; align-items: center; justify-content: center;
                    width: 32px; height: 32px;
                    border: 1px solid #d1d5db; border-radius: 6px;
                    background: #fff; cursor: pointer;
                }
                .btn-page:disabled { opacity: 0.4; cursor: not-allowed; }

                /* OTP 모달 */
                .otp-modal-overlay {
                    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(0,0,0,0.5);
                    display: flex; align-items: center; justify-content: center;
                    z-index: 10000;
                }

                .otp-modal-content {
                    background: white; border-radius: 12px;
                    width: 90%; max-width: 400px;
                    box-shadow: 0 10px 25px rgba(0,0,0,0.2);
                    animation: slideIn 0.3s ease-out;
                }

                @keyframes slideIn {
                    from { transform: translateY(-20px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }

                .otp-modal-header {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 20px; border-bottom: 1px solid #e5e7eb;
                }

                .otp-modal-header h3 {
                    margin: 0; font-size: 20px; font-weight: 600; color: #1f2937;
                }

                .otp-modal-close {
                    background: none; border: none; font-size: 28px; color: #6b7280;
                    cursor: pointer; padding: 0; width: 32px; height: 32px;
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 4px; transition: background-color 0.2s;
                }
                .otp-modal-close:hover { background: #f3f4f6; }

                .otp-modal-body { padding: 20px; }

                .otp-modal-footer {
                    display: flex; gap: 10px;
                    padding: 20px; border-top: 1px solid #e5e7eb;
                }

                .otp-modal-footer .btn-transfer { flex: 1; margin: 0; }

                @media (max-width: 768px) {
                    .settlement-grid { grid-template-columns: 1fr; }
                    .direct-settlement-container { padding: 16px; }
                    .summary-bar { flex-direction: column; gap: 8px; }
                    .form-row { flex-direction: column; }
                }
            `}</style>
        </div>
    );
}

export default DirectSettlement;
